/**
 * CanvasAnalytics.jsx
 * Canvas LMS grade analytics with PII protection + PDF report export.
 *
 * PII Policy:
 *   - Student/staff names are dehydrated (replaced with Canvas IDs) BEFORE
 *     any data or query reaches the LLM.
 *   - Names are rehydrated in the displayed Analysis text only, AFTER the
 *     LLM response is received. Raw Data tab stays anonymized.
 *   - All other PII (emails, login IDs, avatars) stripped at ingest by scrubPII().
 *   - Nothing identifiable is sent to the LLM or written into a PDF.
 *
 * Name index: loaded from Firestore /students and /staff on connect.
 * @ mention: type @ in query box to get a searchable name picker.
 * Fuzzy match: ambiguous names trigger a disambiguation dialog before submit.
 *
 * Course selection:
 *   - Keyword search: type 2+ chars to find matching courses in the term.
 *   - "All courses in term": fetches every course in the selected term
 *     (paginated) and runs the query across all of them simultaneously.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

// ─── PROXY HELPERS ────────────────────────────────────────────────────────────
const PROXY_BASE = process.env.REACT_APP_PROXY_URL ||
  "https://us-central1-kings-app-74847.cloudfunctions.net";

async function getFirebaseToken() {
  const auth = getAuth();
  if (auth.currentUser) return auth.currentUser.getIdToken();
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error("Auth timeout — please sign in.")), 5000);
    const unsubscribe = onAuthStateChanged(auth, user => {
      clearTimeout(timeout); unsubscribe();
      if (user) resolve(user.getIdToken());
      else reject(new Error("Not authenticated — please sign in."));
    });
  });
}

async function proxyFetch(path, params = {}) {
  const idToken = await getFirebaseToken();
  const query = new URLSearchParams({ path, per_page: "100" });
  for (const [k, v] of Object.entries(params)) {
    if (Array.isArray(v)) v.forEach(i => query.append(k, i));
    else query.set(k, v);
  }
  const res = await fetch(`${PROXY_BASE}/canvasProxy?${query}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!res.ok) throw new Error(`Canvas proxy ${res.status}: ${await res.text()}`);
  return { data: await res.json(), linkHeader: res.headers.get("X-Canvas-Link") || "" };
}

async function proxyAnthropic(body) {
  const idToken = await getFirebaseToken();
  const res = await fetch(`${PROXY_BASE}/anthropicProxy`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Anthropic proxy ${res.status}: ${await res.text()}`);
  return res.json();
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const FONT_DISPLAY = "'Playfair Display', Georgia, serif";
const FONT_DATA    = "'Rajdhani', 'Trebuchet MS', sans-serif";
const FONT_BODY    = "'EB Garamond', Georgia, serif";

const C = {
  ink:    "#1a1714",
  cream:  "#faf7f2",
  gold:   "#b8924a",
  navy:   "#1a3a5c",
  muted:  "#7a7068",
  border: "#e8e2d8",
  bg:     "#f4f0e8",
  green:  "#22863a",
  red:    "#8b2c2c",
};

// PDF color arrays
const NAVY_P  = [26, 58, 92];
const GOLD_P  = [184, 146, 74];
const INK_P   = [26, 23, 20];
const MUTED_P = [122, 112, 104];
const GREEN_P = [34, 134, 58];

// ─── PII SCRUBBER ─────────────────────────────────────────────────────────────
function scrubPII(obj) {
  if (Array.isArray(obj)) return obj.map(scrubPII);
  if (obj && typeof obj === "object") {
    const allowed = new Set([
      "sis_user_id", "sis_course_id", "sis_section_id",
      "course_code",
    ]);
    const blocked = new Set([
      "name", "short_name", // Block ALL name fields
      "sortable_name", "display_name", "full_name",
      "email", "login_id", "sis_import_id",
      "bio", "avatar_url", "avatar_image_url", "phone",
      "address", "pronouns", "unique_id", "integration_id",
    ]);
    const clean = {};
    for (const [k, v] of Object.entries(obj)) {
      if (blocked.has(k)) continue;
      if (allowed.has(k)) { clean[k] = v; continue; }
      clean[k] = scrubPII(v);
    }
    return clean;
  }
  return obj;
}

// ─── CANVAS API CLIENT ────────────────────────────────────────────────────────
async function canvasFetch(path, params = {}) {
  let results = [];
  let nextPath = path;
  let isFirst  = true;
  while (nextPath) {
    const { data: page, linkHeader } = await proxyFetch(nextPath, isFirst ? params : {});
    results = results.concat(Array.isArray(page) ? page : [page]);
    const m = linkHeader.match(/\[PROXY\]([^,>]+)[^>]*rel="next"/);
    nextPath = m ? m[1].trim() : null;
    isFirst  = false;
  }
  return scrubPII(results);
}

// Like canvasFetch but also returns a canvasUserId→name map extracted BEFORE scrubbing.
// Use this for enrollment fetches so rehydration can work.
async function canvasFetchWithNameMap(path, params = {}) {
  let results = [];
  let nextPath = path;
  let isFirst  = true;
  while (nextPath) {
    const { data: page, linkHeader } = await proxyFetch(nextPath, isFirst ? params : {});
    results = results.concat(Array.isArray(page) ? page : [page]);
    const m = linkHeader.match(/\[PROXY\]([^,>]+)[^>]*rel="next"/);
    nextPath = m ? m[1].trim() : null;
    isFirst  = false;
  }
  // Extract canvasUserId → name AND sisUserId → name BEFORE scrubPII strips name fields
  const nameMap = {};
  const sisToCanvasId = {};
  results.forEach(item => {
    const name = item.user?.name;
    if (!name) return;
    if (item.user_id) nameMap[String(item.user_id)] = name;
    if (item.sis_user_id) nameMap[String(item.sis_user_id)] = name;
    if (item.user?.sis_user_id) nameMap[String(item.user.sis_user_id)] = name;
    if (item.user_id && item.sis_user_id) {
      sisToCanvasId[String(item.sis_user_id)] = String(item.user_id);
    }
    if (item.user_id && item.user?.sis_user_id) {
      sisToCanvasId[String(item.user.sis_user_id)] = String(item.user_id);
    }
  });
  return { data: scrubPII(results), nameMap, sisToCanvasId };
}

// ─── FETCH ALL COURSES IN TERM (paginated) ───────────────────────────────────
// Fetches every available course in the given term across all pages.
async function fetchAllCoursesInTerm(termId) {
  let all = [];
  let nextPath = "/accounts/1/courses";
  let isFirst = true;
  let pageCount = 0;
  
  console.log("🔍 Starting fetchAllCoursesInTerm for term:", termId);
  
  while (nextPath) {
    pageCount++;
    console.log(`📄 Page ${pageCount}: Fetching ${nextPath}`);
    
const params = isFirst ? {
  per_page: "200",  // Try a bigger page size
  "state[]": ["available", "completed", "unpublished"],
  enrollment_term_id: String(termId),
} : {};
    
    console.log("📤 Request params:", params);
    
    const { data, linkHeader } = await proxyFetch(nextPath, params);
    const arr = Array.isArray(data) ? data : [];
    
    console.log(`📥 Got ${arr.length} courses`);
    console.log("🔗 Link header:", linkHeader);
    
    // Show first few course names to verify we're getting the right data
    if (arr.length > 0) {
      console.log("📚 Sample courses:", arr.slice(0, 3).map(c => c.name));
    }
    
    all = all.concat(arr);
    
    // Extract next path from Link header
    const nextMatch = linkHeader.match(/\[PROXY\]([^,>]+)[^>]*rel="next"/);
    nextPath = nextMatch ? nextMatch[1].trim() : null;
    
    console.log("➡️  Next path:", nextPath);
    
    isFirst = false;
    
    // Safety valve to prevent infinite loops
    if (pageCount > 20) {
      console.error("🚨 Too many pages, breaking!");
      break;
    }
  }
  
  console.log(`✅ Total courses fetched: ${all.length} across ${pageCount} pages`);
  
  const filtered = dedupeAndSortCourses(all);
  console.log(`🧹 After dedup/filtering: ${filtered.length} courses`);
  
  return filtered;
}
 
// ALSO TRY: Check what Canvas is returning for a single page request
async function debugSinglePage(termId) {
  console.log("🔍 DEBUG: Single page request");
  
const params = isFirst ? {
  per_page: "200",
  "state[]": ["available", "completed", "unpublished"],
  // REMOVE THIS LINE: enrollment_term_id: String(termId),
} : {};
  
  console.log(`📥 Single page returned: ${data.length} courses`);
  console.log("🔗 Link header:", linkHeader);
  console.log("📚 Course names:", data.map(c => c.name).slice(0, 10));
  
  return data;
}

async function debugCanvasHeaders(termId) {
  console.log("🔍 DEBUG: Raw Canvas headers");
  
  const idToken = await getFirebaseToken();
  const query = new URLSearchParams({ 
    path: "/accounts/1/courses",
    per_page: "100",
    "state[]": "available",
    enrollment_term_id: String(termId)
  });
  
  const res = await fetch(`${PROXY_BASE}/canvasProxy?${query}`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  
  console.log("📡 Response status:", res.status);
  console.log("📡 X-Canvas-Link header:", res.headers.get("X-Canvas-Link"));
  console.log("📡 Link header:", res.headers.get("Link"));
  console.log("📡 All response headers:");
  for (const [key, value] of res.headers.entries()) {
    console.log(`  ${key}: ${value}`);
  }
  
  const data = await res.json();
  console.log("📡 Data length:", data.length);
  
  return data;
}

// ─── NAME INDEX (Firestore) ───────────────────────────────────────────────────
async function loadNameIndex() {
  const db = getFirestore();
  const index = {};

  const [studentsSnap, staffSnap] = await Promise.all([
    getDocs(collection(db, "students")),
    getDocs(collection(db, "staff")),
  ]);

  studentsSnap.forEach(doc => {
    const d = doc.data();
    const sisId = String(doc.id);
    const name = d.name || `${d.firstName || ""} ${d.lastName || ""}`.trim();
    if (name) index[sisId] = { name, firstName: d.firstName, lastName: d.lastName, grade: d.grade, type: "student", sisId };
  });

  staffSnap.forEach(doc => {
    const d = doc.data();
    const sisId = String(doc.id);
    const name = d.name || `${d.firstName || ""} ${d.lastName || ""}`.trim();
    if (name) index[sisId] = { name, firstName: d.firstName, lastName: d.lastName, grade: null, type: "staff", sisId };
  });

  return index;
}

// ─── FUZZY MATCHING ───────────────────────────────────────────────────────────
function similarity(a, b) {
  a = a.toLowerCase().trim();
  b = b.toLowerCase().trim();
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const trigrams = s => {
    const set = new Set();
    const padded = `  ${s}  `;
    for (let i = 0; i < padded.length - 2; i++) set.add(padded.slice(i, i + 3));
    return set;
  };
  const ta = trigrams(a);
  const tb = trigrams(b);
  let intersection = 0;
  ta.forEach(t => { if (tb.has(t)) intersection++; });
  return (2 * intersection) / (ta.size + tb.size);
}

function detectNames(queryText, nameIndex) {
  const words = queryText.split(/\s+/);
  const people = Object.entries(nameIndex);
  const detections = [];

  for (let len = 3; len >= 1; len--) {
    for (let i = 0; i <= words.length - len; i++) {
      const phrase = words.slice(i, i + len).join(" ");
      if (phrase.length < 3) continue;
      const alreadyCovered = detections.some(d =>
        queryText.indexOf(phrase) >= queryText.indexOf(d.original) &&
        queryText.indexOf(phrase) + phrase.length <= queryText.indexOf(d.original) + d.original.length
      );
      if (alreadyCovered) continue;

      const matches = people
        .map(([canvasId, person]) => ({ canvasId, name: person.name, score: similarity(phrase, person.name) }))
        .filter(m => m.score >= 0.6)
        .sort((a, b) => b.score - a.score)
        .slice(0, 4);

      if (matches.length > 0) {
        detections.push({ original: phrase, matches });
      }
    }
  }
  return detections;
}

function dehydrateQuery(queryText, resolutions, nameIndex) {
  let result = queryText;
  for (const [original, sisId] of Object.entries(resolutions)) {
    const person = nameIndex[sisId];
    const role = person?.type === "staff" ? "teacher" : "student";
    // Use both IDs so LLM can find the person with either system
    result = result.replace(
      new RegExp(original.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), 
      `${role} ${sisId} (Canvas ID may vary)`
    );
  }
  return result;
}

function rehydrateText(text, nameIndex) {
  let result = text;
  for (const [canvasId, person] of Object.entries(nameIndex)) {
      const patterns = [
        new RegExp(`\\bstudent\\s+${canvasId}\\b`, "gi"),
        new RegExp(`\\bteacher\\s+${canvasId}\\b`, "gi"), // Add this line
        new RegExp(`\\buser\\s+${canvasId}\\b`, "gi"),
        new RegExp(`\\bID\\s+${canvasId}\\b`, "gi"),
        new RegExp(`\\b${canvasId}\\b`, "g"),
      ];
    patterns.forEach(pattern => {
      result = result.replace(pattern, () => person.name);
    });
  }
  return result;
}

// ─── LLM: QUERY PLANNER ───────────────────────────────────────────────────────
// ─── LLM: QUERY PLANNER ───────────────────────────────────────────────────────
async function planQuery(userQuery, courseId, sisToCanvasId = {}) {
  const system = `You are a Canvas LMS data analyst. Given a natural-language question about course id ${courseId}, respond ONLY with valid JSON — no markdown, no preamble:
{
  "endpoints": [
    { "path": "/courses/${courseId}/enrollments", "params": {}, "label": "short description" }
  ],
  "analysisInstructions": "what to do with the data"
}
When building endpoints with user IDs, use Canvas user_id values, not SIS IDs.
Common paths (always use course id ${courseId}):
  /courses/${courseId}/enrollments           params: "type[]":"StudentEnrollment", "include[]":"grades"
  /courses/${courseId}/assignments           params: "order_by":"due_at"
  /courses/${courseId}/students/submissions  params: "student_ids[]":"all", "include[]":"assignment"
  /courses/${courseId}/sections              params: "include[]":"enrollments"
  /courses/${courseId}/analytics/student_summaries
  /courses/${courseId}/assignment_groups
Return ONLY the JSON object.`;

  const data = await proxyAnthropic({
    model: "claude-sonnet-4-6",
    max_tokens: 1000,
    system,
    messages: [{ role: "user", content: userQuery }],
  });

  const text = data.content?.find(b => b.type === "text")?.text || "{}";
  let plan = JSON.parse(text.replace(/```json|```/g, "").trim());

  // Fix any user_id parameters in the plan by mapping SIS IDs to Canvas IDs
  plan.endpoints = plan.endpoints.map(endpoint => {
    if (endpoint.params && endpoint.params.user_id) {
      const sisId = endpoint.params.user_id;
      const canvasId = sisToCanvasId[sisId] || sisId;
      console.log(`🔧 Mapping user_id: ${sisId} → ${canvasId}`);
      endpoint.params.user_id = canvasId;
    }
    // Also handle student_ids arrays
    if (endpoint.params && endpoint.params["student_ids[]"]) {
      const studentIds = Array.isArray(endpoint.params["student_ids[]"])
        ? endpoint.params["student_ids[]"]
        : [endpoint.params["student_ids[]"]];

      endpoint.params["student_ids[]"] = studentIds.map(sisId => {
        if (sisId === "all") return sisId;
        const canvasId = sisToCanvasId[sisId] || sisId;
        console.log(`🔧 Mapping student_ids[]: ${sisId} → ${canvasId}`);
        return canvasId;
      });
    }
    return endpoint;
  });

  console.log("📤 Final plan being executed:");
  console.log(JSON.stringify(plan, null, 2));
  plan.endpoints.forEach((endpoint, i) => {
    console.log(`🎯 Endpoint ${i + 1}: ${endpoint.path}`);
    console.log(`   Params:`, endpoint.params);
  });

  return plan;
}

// ─── LLM: DATA ANALYST ────────────────────────────────────────────────────────
async function analyzeData(userQuery, fetchedData, history, isAllCourses = false, includeCharts = false) {
  const chartInstruction = includeCharts ? `
When a chart would help illustrate the data, include it by placing [chart] in your narrative where the chart should appear, and append a JSON block at the end of your response in this exact format:
CHARTS_JSON:{"charts":[{"type":"bar","title":"Chart Title","labels":["A","B"],"datasets":[{"label":"Score","data":[85,72]}]}]}
Chart types: "bar", "line", "pie". Only include charts when they genuinely add value.` : "";

  const system = isAllCourses
    ? `You are an expert academic data analyst for a school using Canvas LMS.
You have been given grade data across ALL courses in the current term. Each entry contains enrollments with grades keyed by course name.
Analyze the data to answer the question. When searching for a person by ID, check BOTH the user_id field AND the sis_user_id field in enrollment records, as the same person may appear under different ID systems.
Be specific and data-driven. Identify course names, patterns, and outliers clearly.
Use plain text with clear structure. No markdown bold (**) or bullet symbols.${chartInstruction}`
    : `You are an expert academic data analyst for a school using Canvas LMS.
All data is fully anonymized — students appear ONLY as numeric IDs.
When searching for a person by ID, check BOTH the user_id field AND the sis_user_id field in enrollment records.
Be specific, data-driven, and concise. Note patterns, outliers, and concerns where relevant.
Use plain text with clear structure. No markdown bold (**) or bullet symbols.${chartInstruction}`;

  const messages = [
    ...history.slice(-6).map(h => ({ role: h.role, content: h.content })),
    {
      role: "user",
      content: `Question: ${userQuery}\n\nAnonymized Canvas data (no PII):\n${JSON.stringify(fetchedData, null, 2).slice(0, 50000)}`,
    },
  ];

  const data = await proxyAnthropic({
    model: "claude-sonnet-4-6",
    max_tokens: 2000,
    system,
    messages,
  });
  const raw = data.content?.find(b => b.type === "text")?.text || "No response.";

  let narrative = raw;
  let charts = [];
  const chartMatch = raw.match(/CHARTS_JSON:(\{.*\})\s*$/s);
  if (chartMatch) {
    try {
      const parsed = JSON.parse(chartMatch[1]);
      charts = parsed.charts || [];
      narrative = raw.replace(/CHARTS_JSON:.*$/s, "").trim();
    } catch { /* ignore parse errors */ }
  }
  return { narrative, charts };
}

// ─── PDF HELPERS ──────────────────────────────────────────────────────────────
const getJSPDF = async () => {
  if (window.jspdf?.jsPDF) return window.jspdf.jsPDF;
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  if (!window.jspdf?.jsPDF) throw new Error("jsPDF failed to load");
  return window.jspdf.jsPDF;
};

const getHtml2Canvas = async () => {
  if (window.html2canvas) return window.html2canvas;
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js";
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  if (!window.html2canvas) throw new Error("html2canvas failed to load");
  return window.html2canvas;
};

function addContinuationHeader(doc, W, title) {
  doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, 12, "F");
  doc.setFillColor(...GOLD_P); doc.rect(0, 12, W, 0.5, "F");
  doc.setTextColor(212, 172, 106); doc.setFontSize(7); doc.setFont("helvetica", "bold");
  doc.text(`${title.slice(0, 80)}${title.length > 80 ? "…" : ""}  (continued)`, W / 2, 8, { align: "center" });
}

function addFooters(doc, W, H, label = "Canvas Analytics") {
  const pc = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pc; i++) {
    doc.setPage(i);
    doc.setFillColor(244, 240, 232); doc.rect(0, H - 10, W, 10, "F");
    doc.setFillColor(...GOLD_P); doc.rect(0, H - 10, W, 0.4, "F");
    doc.setFontSize(7); doc.setFont("helvetica", "italic"); doc.setTextColor(...MUTED_P);
    doc.text(`Internal use only · King's Academy ${label} · PII-Free Report`, W / 2, H - 4, { align: "center" });
    doc.text(`${i} / ${pc}`, W - 14, H - 4);
  }
}

async function buildQueryPage(doc, W, H, entry, idx, total, cardNode) {
  const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, 38, "F");
  doc.setFillColor(...GOLD_P); doc.rect(0, 38, W, 1, "F");

  doc.setTextColor(212, 172, 106); doc.setFontSize(7); doc.setFont("helvetica", "bold");
  doc.text("KING'S ACADEMY  ·  CANVAS ANALYTICS  ·  INTERNAL USE ONLY", W / 2, 10, { align: "center" });
  if (total > 1) doc.text(`Query ${idx + 1} of ${total}`, W - 14, 10, { align: "right" });

  const courseLabel = entry.courseName ? `${entry.courseName}  ·  ${now}` : `Course ID: ${entry.courseId}  ·  ${now}`;
  doc.setTextColor(200, 191, 176); doc.setFontSize(7.5); doc.setFont("helvetica", "italic");
  doc.text(courseLabel, W / 2, 17, { align: "center" });

  doc.setTextColor(250, 247, 242); doc.setFontSize(12); doc.setFont("helvetica", "bold");
  const queryLines = doc.splitTextToSize(entry.query, W - 28);
  const queryY = queryLines.length > 1 ? 25 : 28;
  doc.text(queryLines, W / 2, queryY, { align: "center" });

  doc.setFillColor(13, 45, 14);
  doc.roundedRect(12, 43, W - 24, 9, 1, 1, "F");
  doc.setDrawColor(...GREEN_P);
  doc.roundedRect(12, 43, W - 24, 9, 1, 1, "S");
  doc.setTextColor(63, 185, 80); doc.setFontSize(6.5); doc.setFont("helvetica", "bold");
  doc.text("PRIVACY NOTICE: Names, emails and identifiers stripped before analysis. SIS IDs retained for system joins only.", W / 2, 48.5, { align: "center", maxWidth: W - 28 });

  const dataKeys = Object.keys(entry.data);
  const boxCount = Math.min(dataKeys.length, 4);
  const boxW = (W - 24 - (boxCount - 1) * 4) / Math.max(boxCount, 1);
  let bx = 12;
  dataKeys.slice(0, 4).forEach(k => {
    const count = Array.isArray(entry.data[k]) ? entry.data[k].length : 1;
    const labelLines = doc.splitTextToSize(k, boxW - 4);
    const boxH = 14 + Math.max(0, labelLines.length - 1) * 4;
    doc.setFillColor(244, 240, 232); doc.roundedRect(bx, 57, boxW, boxH, 1, 1, "F");
    doc.setDrawColor(...GOLD_P); doc.roundedRect(bx, 57, boxW, boxH, 1, 1, "S");
    doc.setTextColor(...INK_P); doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text(String(count), bx + boxW / 2, 65, { align: "center" });
    doc.setFontSize(5.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED_P);
    doc.text(labelLines, bx + boxW / 2, 69, { align: "center" });
    bx += boxW + 4;
  });

  let y = 78;

  const chartNodes = cardNode?.querySelectorAll?.(".chart-capture-zone > div") || [];
  for (const chartEl of chartNodes) {
    try {
      const h2c = await getHtml2Canvas();
      const canvas = await h2c(chartEl, {
        scale: 2,
        backgroundColor: "#f4f0e8",
        useCORS: true,
        logging: false,
      });
      const imgW = W - 24;
      const imgH = (imgW / canvas.width) * canvas.height;
      if (y + imgH > H - 20) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }
      doc.addImage(canvas.toDataURL("image/png"), "PNG", 12, y, imgW, imgH);
      y += imgH + 8;
    } catch (e) {
      console.warn("Chart capture failed:", e.message);
    }
  }

  if (y > H - 50) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }

  doc.setFillColor(...GOLD_P); doc.rect(12, y, 2.5, 6, "F");
  doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...NAVY_P);
  doc.text("AI ANALYSIS  —  ANONYMIZED DATA ONLY", 18, y + 4.5);
  y += 12;

  const cleanAnalysis = (entry.analysis || "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .trim();

  const paragraphs = cleanAnalysis.split(/\n\n+/);
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...INK_P);
  for (const para of paragraphs) {
    const lines = doc.splitTextToSize(para.trim(), W - 28);
    for (const line of lines) {
      if (y > H - 20) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }
      doc.text(line, 14, y);
      y += 5;
    }
    y += 3;
  }

  y += 4;
  if (y > H - 40) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }

  doc.setFillColor(...NAVY_P); doc.rect(12, y, 2.5, 6, "F");
  doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...NAVY_P);
  doc.text("DATA FETCHED FROM CANVAS (ANONYMIZED)", 18, y + 4.5);
  y += 12;

  for (const [label, records] of Object.entries(entry.data)) {
    if (y > H - 30) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }
    doc.setFillColor(244, 240, 232); doc.rect(12, y, W - 24, 8, "F");
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...INK_P);
    doc.text(`${label}  —  ${Array.isArray(records) ? records.length : 1} record(s)`, 15, y + 5.5);
    y += 11;

    const sample = Array.isArray(records) ? records.slice(0, 3) : [records];
    for (const rec of sample) {
      if (y > H - 20) { doc.addPage(); addContinuationHeader(doc, W, entry.query); y = 20; }
      const str = JSON.stringify(rec);
      const compact = str.length > 140 ? str.slice(0, 137) + "…" : str;
      doc.setFontSize(6.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...MUTED_P);
      doc.text(compact, 15, y, { maxWidth: W - 30 });
      y += 5;
    }
    if (Array.isArray(records) && records.length > 3) {
      doc.setFontSize(6.5); doc.setFont("helvetica", "italic"); doc.setTextColor(...MUTED_P);
      doc.text(`  … and ${records.length - 3} more record(s)`, 15, y);
      y += 5;
    }
    y += 4;
  }
}

// ─── DISAMBIGUATION DIALOG ────────────────────────────────────────────────────
function DisambiguationDialog({ detections, nameIndex, onConfirm, onCancel }) {
  const [selections, setSelections] = useState(() => {
    const init = {};
    detections.forEach(d => {
      init[d.original] = d.matches[0]?.canvasId || "__none__";
    });
    return init;
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(26,23,20,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 4, maxWidth: 540, width: "100%", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden" }}>
        <div style={{ background: C.navy, padding: "16px 22px" }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase", color: C.gold, marginBottom: 4 }}>Name Disambiguation</div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1rem", color: C.cream }}>We found some ambiguity in your query</div>
        </div>

        <div style={{ padding: "20px 22px", maxHeight: 420, overflowY: "auto" }}>
          {detections.map(d => (
            <div key={d.original} style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, color: C.ink, marginBottom: 8 }}>
                <span style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 3, padding: "2px 8px", fontStyle: "italic" }}>"{d.original}"</span>
                <span style={{ color: C.muted, fontWeight: 400, marginLeft: 8 }}>— did you mean:</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {d.matches.map(m => {
                  const person = nameIndex[m.canvasId];
                  const isSelected = selections[d.original] === m.canvasId;
                  return (
                    <label key={m.canvasId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${isSelected ? C.navy : C.border}`, borderRadius: 3, background: isSelected ? "#f0f4f9" : C.cream, cursor: "pointer" }}>
                      <input type="radio" name={d.original} value={m.canvasId}
                        checked={isSelected}
                        onChange={() => setSelections(s => ({ ...s, [d.original]: m.canvasId }))}
                        style={{ accentColor: C.navy }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, color: C.ink }}>{m.name}</div>
                        {person && (
                          <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>
                            {person.type === "student" ? `Student${person.grade ? ` · ${person.grade}` : ""}` : "Staff"} · ID: {m.canvasId}
                          </div>
                        )}
                      </div>
                      <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>{Math.round(m.score * 100)}% match</div>
                    </label>
                  );
                })}
                <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${selections[d.original] === "__none__" ? C.gold : C.border}`, borderRadius: 3, background: selections[d.original] === "__none__" ? "#fffaf0" : C.cream, cursor: "pointer" }}>
                  <input type="radio" name={d.original} value="__none__"
                    checked={selections[d.original] === "__none__"}
                    onChange={() => setSelections(s => ({ ...s, [d.original]: "__none__" }))}
                    style={{ accentColor: C.gold }} />
                  <div style={{ fontFamily: FONT_DATA, fontSize: 12, color: C.muted, fontStyle: "italic" }}>Not a person — search anyway</div>
                </label>
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 10, background: C.bg }}>
          <button onClick={onCancel} style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, padding: "7px 18px", border: `1px solid ${C.border}`, borderRadius: 2, background: C.cream, color: C.muted, cursor: "pointer" }}>
            Cancel
          </button>
          <button onClick={() => onConfirm(selections)} style={{ fontFamily: FONT_DATA, fontSize: 12, fontWeight: 600, padding: "7px 18px", border: "none", borderRadius: 2, background: C.navy, color: C.cream, cursor: "pointer" }}>
            Confirm & Run Query →
          </button>
        </div>
      </div>
    </div>
  );
}

const CHART_COLORS = ["#1a3a5c","#b8924a","#22863a","#8b2c2c","#6b5b95","#4a90a4","#d4a853","#2d6a4f"];

// ─── CHART BLOCK ──────────────────────────────────────────────────────────────
function ChartBlock({ chart }) {
  if (!chart) return null;
  const { type, title, labels = [], datasets = [] } = chart;

  const chartData = labels.map((label, i) => {
    const point = { label };
    datasets.forEach(ds => { point[ds.label || "value"] = ds.data?.[i] ?? 0; });
    return point;
  });

  const pieData = labels.map((label, i) => ({
    name: label,
    value: datasets[0]?.data?.[i] ?? 0,
  }));

  const rotateTicks = labels.length > 5;

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 3, padding: "16px 20px", marginBottom: 16 }}>
      {title && (
        <div style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: C.navy, marginBottom: 12 }}>
          {title}
        </div>
      )}
      <ResponsiveContainer width="100%" height={type === "pie" ? 380 : labels.length > 5 ? 340 : 280}>
        {type === "pie" ? (
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="42%" outerRadius={100} label={false}>
              {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(value) => {
              const total = pieData.reduce((s, d) => s + d.value, 0);
              return [`${value} (${((value / total) * 100).toFixed(1)}%)`, ""];
            }} />
            <Legend verticalAlign="bottom" layout="vertical" align="center"
              formatter={(value) => {
                const total = pieData.reduce((s, d) => s + d.value, 0);
                const d = pieData.find(p => p.name === value);
                return d ? `${value} (${((d.value / total) * 100).toFixed(1)}%)` : value;
              }}
              wrapperStyle={{ fontFamily: FONT_DATA, fontSize: 12, paddingTop: 8 }}
            />
          </PieChart>
        ) : type === "line" ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="label" tick={{ fontFamily: FONT_DATA, fontSize: 10 }} angle={rotateTicks ? -35 : 0} textAnchor={rotateTicks ? "end" : "middle"} interval={0} height={rotateTicks ? 60 : 30} />
            <YAxis tick={{ fontFamily: FONT_DATA, fontSize: 10 }} />
            <Tooltip /><Legend />
            {datasets.map((ds, i) => (
              <Line key={i} type="monotone" dataKey={ds.label || "value"} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        ) : (
          <BarChart data={chartData} margin={{ bottom: rotateTicks ? 40 : 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="label" tick={{ fontFamily: FONT_DATA, fontSize: 10, fill: C.muted }} angle={rotateTicks ? -35 : 0} textAnchor={rotateTicks ? "end" : "middle"} interval={0} height={rotateTicks ? 60 : 30} />
            <YAxis tick={{ fontFamily: FONT_DATA, fontSize: 10 }} />
            <Tooltip />
            <Legend verticalAlign="bottom" wrapperStyle={{ fontFamily: FONT_DATA, fontSize: 11 }} />
            {datasets.map((ds, i) => (
              <Bar key={i} dataKey={ds.label || "value"} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

// ─── RESULT CARD ──────────────────────────────────────────────────────────────
function ResultCard({ entry, idx, total, onDownload, pdfLoading, cardRef }) {
  const [tab, setTab] = useState("analysis");
  const recordCount = Object.values(entry.data).reduce((a, v) => a + (Array.isArray(v) ? v.length : 1), 0);

  return (
    <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 24 }}>
      <div style={{ padding: "14px 22px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10, background: C.bg }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted, marginBottom: 4 }}>
            Query {idx + 1} of {total}  ·  {recordCount} records  ·  {entry.courseId === "__all__" ? "All Courses" : `Course ${entry.courseId}`}
          </div>
          <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: "1.05rem", fontWeight: 600, margin: "0 0 6px", color: C.ink }}>{entry.query}</h2>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {Object.entries(entry.data).map(([label, recs]) => (
              <span key={label} style={{ fontFamily: FONT_DATA, fontSize: 10, padding: "2px 8px", background: C.cream, border: `1px solid ${C.border}`, borderRadius: 10, color: C.muted }}>
                {label}: {Array.isArray(recs) ? recs.length : 1}
              </span>
            ))}
          </div>
        </div>
        <button onClick={() => onDownload(idx)} disabled={pdfLoading}
          style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "7px 16px", border: `1px solid ${C.navy}`, borderRadius: 2, background: pdfLoading ? C.muted : C.navy, color: C.cream, flexShrink: 0, cursor: "pointer" }}>
          {pdfLoading ? "Building…" : "↓ Export PDF"}
        </button>
      </div>

      <div style={{ display: "flex", borderBottom: `1px solid ${C.border}` }}>
        {[["analysis", "AI Analysis"], ["data", "Raw Data (Anonymized)"]].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ flex: 1, padding: "9px 0", fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", border: "none", background: tab === t ? C.cream : "#fff", color: tab === t ? C.ink : C.muted, borderBottom: tab === t ? `2px solid ${C.navy}` : "none", cursor: "pointer" }}>
            {label}
          </button>
        ))}
      </div>

      <div ref={cardRef} style={{ padding: "20px 24px", background: "#fff" }}>
        {tab === "analysis" && (
          <div>
            <div style={{ background: "#0d2d0e", border: "1px solid #238636", borderRadius: 2, padding: "8px 12px", marginBottom: 16, fontFamily: FONT_DATA, fontSize: "0.75rem", color: "#3fb950", display: "flex", gap: 8 }}>
              <span>🔒</span>
              <span>Student names stripped before LLM analysis. Names shown below are rehydrated from your local directory for readability only.</span>
            </div>
            {entry.charts?.length > 0 && (
              <div className="chart-capture-zone">
                {entry.charts.map((chart, i) => <ChartBlock key={i} chart={chart} />)}
              </div>
            )}
            <div style={{ fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.8, color: C.ink, whiteSpace: "pre-wrap" }}>
              {(entry.rehydratedAnalysis || entry.analysis)?.replace(/\[chart\]/g, "").trim()}
            </div>
          </div>
        )}
        {tab === "data" && (
          <pre style={{ margin: 0, fontSize: "0.72rem", fontFamily: FONT_DATA, background: "#f8f6f2", border: `1px solid ${C.border}`, borderRadius: 2, padding: "1rem", overflowX: "auto", maxHeight: 400, overflowY: "auto", color: C.ink, lineHeight: 1.6 }}>
            {JSON.stringify(entry.data, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

// ─── COURSE HELPERS ───────────────────────────────────────────────────────────
function dedupeAndSortCourses(courses) {
  return courses
    .filter(c => {
      const name = (c.name || "").toLowerCase();
      const code = (c.course_code || "").toLowerCase();
      if (name.includes("advisory")) return false;
      if (name.includes("sandbox")) return false;
      if (code.includes("sandbox")) return false;
      return true;
    })
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

function formatTermName(name) {
  return name.replace(/\b(20(\d{2}))\b/g, (_, full, yy) => {
    const next = String(parseInt(yy) + 1).padStart(2, "0");
    return `${yy}/${next}`;
  });
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { label: "Final Grades",  path: "/courses/:id/enrollments",         params: { "type[]": "StudentEnrollment", "include[]": "grades" } },
  { label: "Assignments",   path: "/courses/:id/assignments",          params: { order_by: "due_at" } },
  { label: "Submissions",   path: "/courses/:id/students/submissions", params: { "student_ids[]": "all", "include[]": "assignment" } },
  { label: "Sections",      path: "/courses/:id/sections",            params: { "include[]": "enrollments" } },
];

const EXAMPLE_QUERIES = [
  "Which students have a final grade below 70%?",
  "Show me missing and late submissions",
  "What's the grade distribution across sections?",
  "Which assignments have the lowest average scores?",
  "Compare performance across assignment groups",
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function CanvasAnalytics() {
  const domain = process.env.REACT_APP_CANVAS_DOMAIN || "";

  const [connected,      setConnected]      = useState(false);
  const [courses,        setCourses]        = useState([]);
  const [courseId,       setCourseId]       = useState("");
  const [terms,          setTerms]          = useState([]);
  const [selectedTermId, setSelectedTermId] = useState("");
  const [courseSearch,   setCourseSearch]   = useState("");
  const [allTermCourses, setAllTermCourses] = useState([]); // full course list for __all__ mode
  const [loadingAllCourses, setLoadingAllCourses] = useState(false);

  const [query,        setQuery]        = useState("");
  const [results,      setResults]      = useState([]);
  const [history,      setHistory]      = useState([]);
  const [fetching,     setFetching]     = useState(false);
  const [pdfLoading,   setPdfLoading]   = useState(false);
  const [includeCharts, setIncludeCharts] = useState(false);
  const [error,        setError]        = useState("");
  const [log,          setLog]          = useState([]);
  const [showThread,   setShowThread]   = useState(false);
  const [nameIndex,    setNameIndex]    = useState({});
  const [nameLoading,  setNameLoading]  = useState(false);
  const [runtimeNameMap, setRuntimeNameMap] = useState({});
  const [sisToCanvasId, setSisToCanvasId]   = useState({});

  // @ mention state
  const [mentionOpen,    setMentionOpen]    = useState(false);
  const [mentionSearch,  setMentionSearch]  = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionPos,     setMentionPos]     = useState({ top: 0, left: 0 });
  const [mentionTriggerIdx, setMentionTriggerIdx] = useState(-1);

  // Disambiguation state
  const [disambig,     setDisambig]     = useState(null);
  const [pendingQuery, setPendingQuery] = useState("");

  const cardRefs    = useRef([]);
  const logRef      = useRef(null);
  const textareaRef = useRef(null);

  const addLog = (msg, type = "info") => {
    setLog(prev => [...prev, { msg, type, ts: new Date().toLocaleTimeString() }]);
    setTimeout(() => logRef.current?.scrollTo(0, 99999), 50);
  };

  // ── Load name index on connect ────────────────────────────────────────────
  const loadNames = async () => {
    setNameLoading(true);
    try {
      const idx = await loadNameIndex();
      setNameIndex(idx);
      addLog(`Name index loaded — ${Object.keys(idx).length} people.`, "success");
    } catch (e) {
      addLog(`Name index failed: ${e.message}`, "error");
    } finally {
      setNameLoading(false);
    }
  };

  // ── Connect ───────────────────────────────────────────────────────────────
  const handleConnect = async () => {
    setError(""); setFetching(true);
    addLog("Connecting to Canvas…");
    try {
      const termRes = await proxyFetch("/accounts/1/terms", { per_page: "100" });
      const allTerms = (termRes.data?.enrollment_terms || [])
        .filter(t => t.name !== "Default Term")
        .sort((a, b) => (b.start_at || "").localeCompare(a.start_at || ""));
      setTerms(allTerms);

      const defaultTerm = allTerms.find(t => t.sis_term_id === "50.2025");
      const termId = defaultTerm?.id || allTerms[0]?.id || "";
      setSelectedTermId(String(termId));

      setConnected(true);
      addLog(`Connected — select a term and search for courses.`, "success");
      await loadNames();
    } catch (e) {
      setError(e.message); addLog(e.message, "error");
    } finally { setFetching(false); }
  };

  // ── Term change ───────────────────────────────────────────────────────────
  const handleTermChange = async (termId) => {
    setSelectedTermId(termId);
    setCourseId("");
    setCourses([]);
    setCourseSearch("");
    setAllTermCourses([]);
  };

  // ── Keyword course search ─────────────────────────────────────────────────
  const courseSearchTimeout = useRef(null);
  const handleCourseSearch = (searchTerm) => {
    setCourseSearch(searchTerm);
    setCourseId("");
    clearTimeout(courseSearchTimeout.current);
    if (searchTerm.length < 2) { setCourses([]); return; }
    courseSearchTimeout.current = setTimeout(async () => {
      setFetching(true);
      try {
        const data = await canvasFetch("/accounts/1/courses", {
          per_page: "50",
          "state[]": "available",
          enrollment_term_id: String(selectedTermId),
          search_term: searchTerm,
        });
        setCourses(dedupeAndSortCourses(data));
      } catch (e) {
        setError(e.message);
      } finally { setFetching(false); }
    }, 400);
  };

  // ── Load ALL courses in current term ─────────────────────────────────────
  const handleSelectAllCourses = async () => {
  if (!selectedTermId) { setError("Select a term first."); return; }
  setLoadingAllCourses(true);
  setError("");
  addLog(`Loading all courses in term ${selectedTermId}…`);
  
  try {
    // ADD THIS LINE to debug headers:
    await debugCanvasHeaders(selectedTermId);
    
    const all = await fetchAllCoursesInTerm(selectedTermId);
    setAllTermCourses(all);
    setCourses(all);
    setCourseId("__all__");
    setCourseSearch("");
    addLog(`Loaded ${all.length} courses in term — ready for cross-course queries.`, "success");
  } catch (e) {
    setError(e.message);
    addLog(e.message, "error");
  } finally { 
    setLoadingAllCourses(false); 
  }
};

const handleSelectVisibleCourses = () => {
  if (courses.length === 0) {
    setError("No courses found in current search.");
    return;
  }
  
  // Set all currently visible courses as selected
  setAllTermCourses(courses);
  setCourseId("__all__");
  addLog(`Selected ${courses.length} visible courses for cross-course queries.`, "success");
};

  // ── @ mention ─────────────────────────────────────────────────────────────
  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    const cursor = e.target.selectionStart;
    const textBefore = val.slice(0, cursor);
    const atMatch = textBefore.match(/@(\w*)$/);

    if (atMatch) {
      const search = atMatch[1].toLowerCase();
      setMentionSearch(search);
      setMentionTriggerIdx(cursor - atMatch[0].length);

      const filtered = Object.entries(nameIndex)
        .filter(([, p]) => p.name.toLowerCase().includes(search))
        .slice(0, 8)
        .map(([id, p]) => ({ id, ...p }));
      setMentionResults(filtered);
      setMentionOpen(filtered.length > 0);

      if (textareaRef.current) {
        const rect = textareaRef.current.getBoundingClientRect();
        setMentionPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
      }
    } else {
      setMentionOpen(false);
    }
  };

  const handleMentionSelect = (person) => {
    const before = query.slice(0, mentionTriggerIdx);
    const after  = query.slice(textareaRef.current?.selectionStart || query.length);
    setQuery(`${before}${person.name}${after}`);
    setMentionOpen(false);
    textareaRef.current?.focus();
  };

  // ── Submit query ──────────────────────────────────────────────────────────
  const handleQuery = useCallback(async (override, resolvedSelections) => {
    const q = (override || query).trim();
    if (!q || !courseId || !connected) return;

    if (!resolvedSelections) {
      const detections = detectNames(q, nameIndex).filter(d =>
        d.matches[0]?.score < 0.95
      );
      const exactMatches = detectNames(q, nameIndex).filter(d => d.matches[0]?.score >= 0.95);

      if (detections.length > 0) {
        setPendingQuery(q);
        setDisambig(detections);
        return;
      }

      if (exactMatches.length > 0) {
        const auto = {};
        exactMatches.forEach(d => { auto[d.original] = d.matches[0].canvasId; });
        return handleQuery(q, auto);
      }
    }

    setError(""); setFetching(true);
    setQuery("");
    setDisambig(null);
    setPendingQuery("");

    console.log("🔍 sisToCanvasId mapping:", sisToCanvasId);
    console.log("🔍 Looking for sisId 100495, found canvasId:", sisToCanvasId["100495"]);
    
    const dehydrationMap = {};
    let dehydratedQuery = q;

    if (resolvedSelections) {
      const resolutions = {};
      for (const [original, sisId] of Object.entries(resolvedSelections)) {
        if (sisId !== "__none__" && nameIndex[sisId]) {
          const canvasUserId = sisToCanvasId[sisId] || sisId;
          resolutions[original] = canvasUserId;
          dehydrationMap[canvasUserId] = nameIndex[sisId].name;
          dehydrationMap[sisId] = nameIndex[sisId].name;
        }
      }
    dehydratedQuery = dehydrateQuery(q, resolutions, nameIndex);    
}

    const isAllCourses = courseId === "__all__";
    // For all-courses mode use allTermCourses; for keyword mode use courses
    const courseList = isAllCourses ? allTermCourses : courses;

    addLog(isAllCourses
      ? `Cross-course query across ${courseList.length} courses…`
      : `Planning: "${dehydratedQuery}"`);

    const prevHistory = history;
    setHistory(prev => [...prev, { role: "user", content: dehydratedQuery }]);

    try {
      let allData = {};
      let accumulatedNameMap = {};
      let accumulatedSisMap = {};

      if (isAllCourses) {
        addLog(`Fetching grades across ${courseList.length} courses…`);
        const courseResults = [];
        const batchSize = 5;
        for (let i = 0; i < courseList.length; i += batchSize) {
          const batch = courseList.slice(i, i + batchSize);
          const batchResults = await Promise.all(
            batch.map(async c => {
              try {
                // Fetch both student and teacher enrollments
const [studentEnrollments, teacherEnrollments] = await Promise.all([
  canvasFetch(`/courses/${c.id}/enrollments`, {
    "type[]": "StudentEnrollment", 
    "include[]": "grades", 
    per_page: "100"
  }),
  canvasFetch(`/courses/${c.id}/enrollments`, {
    "type[]": "TeacherEnrollment", 
    per_page: "100"
  })
]);

const allEnrollments = [...studentEnrollments, ...teacherEnrollments];
if (c.name && c.name.toLowerCase().includes('spanish')) {
  console.log(`📚 Spanish course: ${c.name} (ID: ${c.id})`);
  console.log("Sample enrollment record:", allEnrollments[0]);
  console.log("Student user_ids:", studentEnrollments.map(e => e.user_id));
  console.log("Student sis_user_ids:", studentEnrollments.map(e => e.sis_user_id || 'none'));
  
  // Check if our target student is in this course
  const targetSisId = "100495";
  const foundBySis = studentEnrollments.find(e => e.sis_user_id === targetSisId);
  const foundByUserId = studentEnrollments.find(e => e.user_id === targetSisId);
  
  if (foundBySis) {
    console.log(`✅ Found student ${targetSisId} by SIS ID! Canvas user_id: ${foundBySis.user_id}`);
  } else if (foundByUserId) {
    console.log(`✅ Found student ${targetSisId} by Canvas user_id!`);
  } else {
    console.log(`❌ Student ${targetSisId} not found in ${c.name}`);
  }
}
                return { 
  courseId: c.id, 
  courseName: c.name, 
  enrollments: allEnrollments 
};
              } catch (error) {
  console.log(`❌ Failed to fetch course ${c.id} (${c.name}):`, error.message);
  addLog(`  → Course ${c.name} failed: ${error.message}`, "error");
  return { courseId: c.id, courseName: c.name, enrollments: [], error: error.message };
}
            })
          );
          courseResults.push(...batchResults);
          addLog(`  → ${Math.min(i + batchSize, courseList.length)}/${courseList.length} done`, "info");
        }
        const validCourseResults = courseResults.filter(course => 
  course.enrollments && course.enrollments.length > 0
);
console.log(`📊 Successfully fetched ${validCourseResults.length} out of ${courseResults.length} courses`);
addLog(`📊 ${validCourseResults.length} courses have enrollment data, ${courseResults.length - validCourseResults.length} were empty/failed`, "info");
allData = { cross_course_grades: validCourseResults };
        addLog("All course data fetched.", "success");
      } else {
          const plan = await planQuery(dehydratedQuery, courseId, sisToCanvasId);        
          addLog(`Plan ready — ${plan.endpoints?.length || 0} endpoint(s)`);
        for (const ep of (plan.endpoints || [])) {
          const cleanPath = ep.path.replace(/^\/api\/v1/, "");
          addLog(`Fetching ${cleanPath}…`);
          const { data: result, nameMap, sisToCanvasId: newSisMap } = await canvasFetchWithNameMap(cleanPath, ep.params || {});
          Object.assign(accumulatedNameMap, nameMap);
          Object.assign(accumulatedSisMap, newSisMap);
          allData[ep.label || cleanPath] = result;
          addLog(`  → ${Array.isArray(result) ? result.length : 1} record(s)`, "success");
        }
        setRuntimeNameMap(prev => ({ ...prev, ...accumulatedNameMap }));
        setSisToCanvasId(prev => ({ ...prev, ...accumulatedSisMap }));
      }

      setFetching(false);
      addLog("Analyzing with LLM…");

      const { narrative: analysis, charts } = await analyzeData(dehydratedQuery, allData, prevHistory, isAllCourses, includeCharts);

      const combinedNameMap = {
        ...Object.fromEntries(Object.entries(nameIndex).map(([id, p]) => [id, { name: p.name }])),
        ...Object.fromEntries(Object.entries({ ...runtimeNameMap, ...accumulatedNameMap || {} }).map(([id, name]) => [id, { name }])),
      };

      const rehydratedAnalysis = rehydrateText(analysis, combinedNameMap);

      const termName = formatTermName(terms.find(t => String(t.id) === String(selectedTermId))?.name || "");
      const courseName = isAllCourses
        ? `All Courses — ${termName}`
        : courses.find(c => String(c.id) === String(courseId))?.name || null;

      const entry = { query: q, courseId, courseName, data: allData, analysis, rehydratedAnalysis, charts };
      setResults(prev => [...prev, entry]);
      setHistory(prev => [...prev, { role: "assistant", content: analysis }]);
      addLog("Done.", "success");
    } catch (e) {
      setError(e.message); addLog(e.message, "error");
      setHistory(prevHistory);
    } finally { setFetching(false); }
  }, [query, courseId, connected, history, nameIndex, courses, allTermCourses, terms, selectedTermId, runtimeNameMap, sisToCanvasId, includeCharts]);

  // ── Quick fetch ───────────────────────────────────────────────────────────
  const handleQuickFetch = async (action) => {
    if (!courseId || courseId === "__all__") { setError("Select a specific course for quick fetch."); return; }
    setFetching(true); setError("");
    addLog(`Quick fetch: ${action.label}`);
    try {
      const path = action.path.replace(":id", courseId);
      const { data, nameMap, sisToCanvasId: newSisMap } = await canvasFetchWithNameMap(path, action.params);
      setRuntimeNameMap(prev => ({ ...prev, ...nameMap }));
      setSisToCanvasId(prev => ({ ...prev, ...newSisMap }));
      addLog(`  → ${data.length} record(s)`, "success");
      addLog("Analyzing…");

      const q = `Quick fetch: ${action.label} for course ${courseId}`;
      const allData = { [action.label]: data };
      const { narrative: analysis, charts } = await analyzeData(q, allData, history, false, includeCharts);
      const combinedNameMap = {
        ...Object.fromEntries(Object.entries(nameIndex).map(([id, p]) => [id, { name: p.name }])),
        ...Object.fromEntries(Object.entries({ ...runtimeNameMap, ...nameMap }).map(([id, name]) => [id, { name }])),
      };
      const rehydratedAnalysis = rehydrateText(analysis, combinedNameMap);
      const courseName = courses.find(c => String(c.id) === String(courseId))?.name || null;
      const entry = { query: q, courseId, courseName, data: allData, analysis, rehydratedAnalysis, charts };

      setResults(prev => [...prev, entry]);
      setHistory(prev => [...prev,
        { role: "user",      content: q },
        { role: "assistant", content: analysis },
      ]);
      addLog("Done.", "success");
    } catch (e) {
      setError(e.message); addLog(e.message, "error");
    } finally { setFetching(false); }
  };

  // ── Export single PDF ─────────────────────────────────────────────────────
  const handleDownloadSingle = useCallback(async (idx) => {
    const entry = results[idx];
    const cardNode = cardRefs.current[idx];
    if (!entry) return;
    setPdfLoading(true);
    try {
      const JSPDF = await getJSPDF();
      const doc = new JSPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      await buildQueryPage(doc, W, H, entry, idx, results.length, cardNode);
      addFooters(doc, W, H);
      const safe = entry.query.replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_]/g, "").slice(0, 40);
      doc.save(`KA_Canvas_${safe}.pdf`);
    } catch (e) {
      setError("PDF failed: " + e.message);
    } finally { setPdfLoading(false); }
  }, [results]);

  // ── Export full session report ────────────────────────────────────────────
  const handleDownloadAll = useCallback(async () => {
    if (!results.length) return;
    setPdfLoading(true);
    try {
      const JSPDF = await getJSPDF();
      const doc = new JSPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const now = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

      // Cover page
      doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, H, "F");
      doc.setFillColor(12, 20, 36); doc.rect(0, 0, W, 10, "F");
      doc.setFillColor(...GOLD_P); doc.rect(0, 0, 2, H, "F");
      doc.setFillColor(...GOLD_P); doc.rect(W - 2, 0, 2, H, "F");

      doc.setTextColor(212, 172, 106);
      doc.setFontSize(10); doc.setFont("helvetica", "bold");
      doc.text("KING'S ACADEMY", W / 2, 60, { align: "center", charSpace: 5 });
      doc.setFontSize(7); doc.setFont("helvetica", "normal");
      doc.setTextColor(140, 120, 90);
      doc.text("EST. 1999  ·  MADABA, JORDAN", W / 2, 70, { align: "center", charSpace: 1 });

      doc.setDrawColor(...GOLD_P); doc.setLineWidth(0.4);
      doc.line(W / 2 - 22, 75, W / 2 + 22, 75);

      doc.setTextColor(250, 247, 242);
      doc.setFontSize(30); doc.setFont("helvetica", "bold");
      doc.text("Canvas Analytics", W / 2, 110, { align: "center" });

      doc.setFontSize(13); doc.setFont("helvetica", "italic");
      doc.setTextColor(180, 170, 155);
      doc.text("Grade & Submission Intelligence Report", W / 2, 123, { align: "center" });

      doc.setDrawColor(...GOLD_P); doc.setLineWidth(0.6);
      doc.line(16, 132, W - 16, 132);
      doc.setLineWidth(0.2);
      doc.line(16, 135, W - 16, 135);

      const firstCourseName = results[0]?.courseName;
      if (firstCourseName) {
        doc.setFontSize(11); doc.setFont("helvetica", "normal");
        doc.setTextColor(...GOLD_P);
        doc.text(firstCourseName, W / 2, 148, { align: "center" });
      }

      doc.setFillColor(13, 45, 14);
      doc.roundedRect(W / 2 - 62, 168, 124, 12, 1, 1, "F");
      doc.setDrawColor(34, 134, 58);
      doc.roundedRect(W / 2 - 62, 168, 124, 12, 1, 1, "S");
      doc.setTextColor(63, 185, 80); doc.setFontSize(7); doc.setFont("helvetica", "bold");
      doc.text("PII-FREE  —  No student names or contact info in this report", W / 2, 175.5, { align: "center" });

      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(120, 110, 95);
      doc.text(`${results.length} quer${results.length > 1 ? "ies" : "y"}  ·  ${now}`, W / 2, 190, { align: "center" });

      doc.setFillColor(12, 20, 35); doc.rect(0, H - 14, W, 14, "F");
      doc.setTextColor(80, 70, 60); doc.setFontSize(7); doc.setFont("helvetica", "italic");
      doc.text("Internal use only · King's Academy Canvas Analytics · Confidential", W / 2, H - 5, { align: "center" });

      for (let i = 0; i < results.length; i++) {
        doc.addPage();
        await buildQueryPage(doc, W, H, results[i], i, results.length, cardRefs.current[i]);
      }

      if (history.length > 0) {
        doc.addPage();
        doc.setFillColor(...NAVY_P); doc.rect(0, 0, W, 26, "F");
        doc.setFillColor(...GOLD_P); doc.rect(0, 26, W, 1, "F");
        doc.setTextColor(250, 247, 242); doc.setFontSize(14); doc.setFont("helvetica", "bold");
        doc.text("Conversation Log", 14, 16);
        doc.setTextColor(212, 172, 106); doc.setFontSize(8); doc.setFont("helvetica", "italic");
        doc.text("All queries and AI responses from this session", W - 14, 16, { align: "right" });

        let y = 34;
        for (const msg of history) {
          const isUser = msg.role === "user";
          const cleanContent = (msg.content || "")
            .replace(/\*\*(.+?)\*\*/g, "$1")
            .replace(/\*(.+?)\*/g, "$1");
          const lines = doc.splitTextToSize(cleanContent, W - 36);
          const blockH = lines.length * 4.5 + 14;
          if (y + blockH > H - 16) { doc.addPage(); y = 20; }

          doc.setFillColor(isUser ? 232 : 244, 240, isUser ? 248 : 232);
          doc.rect(12, y, W - 24, blockH, "F");
          doc.setFillColor(...(isUser ? NAVY_P : GOLD_P));
          doc.rect(12, y, 3, blockH, "F");

          doc.setFontSize(7); doc.setFont("helvetica", "bold");
          doc.setTextColor(...(isUser ? NAVY_P : GOLD_P));
          doc.text(isUser ? "QUERY" : "ANALYSIS", 19, y + 5.5);

          doc.setFontSize(8.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...INK_P);
          doc.text(lines, 19, y + 11);
          y += blockH + 6;
        }
      }

      addFooters(doc, W, H);
      doc.save(`KA_CanvasReport_${now.replace(/,?\s+/g, "_")}.pdf`);
    } catch (e) {
      setError("Report failed: " + e.message);
    } finally { setPdfLoading(false); }
  }, [results, history]);

  // ─── STYLES ──────────────────────────────────────────────────────────────
  const S = {
    wrap:       { fontFamily: FONT_BODY, background: C.cream, minHeight: "100vh", color: C.ink },
    header:     { background: C.ink, padding: "2rem 2rem 1.6rem", borderBottom: `3px solid ${C.gold}` },
    inner:      { maxWidth: 980, margin: "0 auto" },
    main:       { maxWidth: 980, margin: "0 auto", padding: "2rem", display: "grid", gridTemplateColumns: "260px 1fr", gap: "1.5rem", alignItems: "start" },
    sidebar:    { position: "sticky", top: "1rem" },
    card:       { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, padding: "1.2rem", marginBottom: "1rem", boxShadow: "0 1px 6px rgba(0,0,0,0.05)" },
    label:      { display: "block", fontFamily: FONT_DATA, fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted, marginBottom: 4 },
    input:      { width: "100%", background: C.cream, border: `1px solid ${C.border}`, borderRadius: 2, padding: "8px 10px", color: C.ink, fontSize: "0.88rem", fontFamily: FONT_BODY, boxSizing: "border-box", marginBottom: 10 },
    btnPrimary: { padding: "7px 16px", borderRadius: 2, border: "none", cursor: "pointer", fontFamily: FONT_DATA, fontSize: "0.82rem", fontWeight: 600, background: C.navy, color: C.cream },
    btnGold:    { padding: "10px 22px", borderRadius: 2, border: "none", cursor: "pointer", fontFamily: FONT_DATA, fontSize: "0.9rem", fontWeight: 600, background: C.gold, color: C.ink },
    btnGhost:   { padding: "7px 12px", borderRadius: 2, border: `1px solid ${C.border}`, cursor: "pointer", fontFamily: FONT_DATA, fontSize: "0.8rem", fontWeight: 500, background: C.bg, color: C.muted, textAlign: "left", width: "100%" },
    piiNotice:  { background: "#0d2d0e", border: "1px solid #238636", borderRadius: 3, padding: "8px 14px", fontFamily: FONT_DATA, fontSize: "0.75rem", color: "#3fb950", marginBottom: "1rem", display: "flex", gap: 8, alignItems: "center" },
    logBox:     { background: "#f8f6f2", border: `1px solid ${C.border}`, borderRadius: 2, padding: 10, maxHeight: 130, overflowY: "auto", fontSize: "0.72rem", fontFamily: FONT_DATA, lineHeight: 1.8 },
    errorBox:   { background: "#fff5f5", border: `1px solid ${C.red}`, borderRadius: 2, padding: "10px 14px", fontFamily: FONT_DATA, fontSize: 13, color: C.red, marginBottom: 16 },
    pill:       { fontFamily: FONT_DATA, fontSize: 11, padding: "4px 11px", border: `1px solid ${C.border}`, borderRadius: 10, background: C.bg, color: C.muted, cursor: "pointer" },
  };

  const hasResults = results.length > 0;
  const isAllMode  = courseId === "__all__";

  return (
    <div style={S.wrap}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Rajdhani:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        button { cursor: pointer; }
        select:focus, input:focus, textarea:focus { outline: 2px solid ${C.navy}; outline-offset: 1px; }
        .mention-item:hover { background: ${C.bg} !important; }
      `}</style>

      {/* Disambiguation dialog */}
      {disambig && (
        <DisambiguationDialog
          detections={disambig}
          nameIndex={nameIndex}
          onConfirm={(selections) => handleQuery(pendingQuery, selections)}
          onCancel={() => { setDisambig(null); setPendingQuery(""); }}
        />
      )}

      {/* @ mention dropdown */}
      {mentionOpen && (
        <div style={{ position: "absolute", top: mentionPos.top, left: mentionPos.left, zIndex: 500, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.12)", minWidth: 260, maxHeight: 260, overflowY: "auto" }}>
          <div style={{ padding: "6px 12px 4px", fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, borderBottom: `1px solid ${C.border}` }}>
            People
          </div>
          {mentionResults.map(person => (
            <div key={person.id} className="mention-item"
              onClick={() => handleMentionSelect(person)}
              style={{ padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, color: C.ink }}>{person.name}</div>
                <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>
                  {person.type === "student" ? `Student${person.grade ? ` · ${person.grade}` : ""}` : "Staff"}
                </div>
              </div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted }}>ID: {person.id}</div>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <header style={S.header}>
        <div style={{ ...S.inner, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.25em", color: C.gold, textTransform: "uppercase", marginBottom: 8 }}>
              King's Academy · Canvas Analytics
            </div>
            <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(1.4rem,3vw,2rem)", fontWeight: 700, color: C.cream, margin: "0 0 6px", lineHeight: 1.15 }}>
              Grade <em style={{ color: C.gold }}>Intelligence</em>
            </h1>
            <p style={{ fontFamily: FONT_BODY, fontStyle: "italic", color: "rgba(250,247,242,0.5)", fontSize: 14, margin: 0 }}>
              Query Canvas in plain English · PII-free analysis · export PDF reports
            </p>
          </div>
          {hasResults && (
            <button onClick={handleDownloadAll} disabled={pdfLoading} style={{ ...S.btnGold, flexShrink: 0, opacity: pdfLoading ? 0.7 : 1 }}>
              {pdfLoading ? "Building…" : `↓ Full Report (${results.length} quer${results.length > 1 ? "ies" : "y"})`}
            </button>
          )}
        </div>
      </header>

      <main style={S.main}>

        {/* Sidebar */}
        <div style={S.sidebar}>

          {/* Connection card */}
          {!connected ? (
            <div style={S.card}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 12 }}>Canvas Connection</div>
              <label style={S.label}>Domain</label>
              <input style={S.input} value={domain} readOnly placeholder="from REACT_APP_CANVAS_DOMAIN" />
              <label style={S.label}>Token</label>
              <input style={{ ...S.input, letterSpacing: "0.08em" }} value="Stored in Firebase secrets" readOnly />
              <button style={{ ...S.btnPrimary, width: "100%", padding: "9px" }} onClick={handleConnect} disabled={fetching}>
                {fetching ? "Connecting…" : "Connect to Canvas"}
              </button>
            </div>
          ) : (
            <div style={{ ...S.card, borderLeft: `3px solid ${C.gold}` }}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 9, color: C.green, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>● Connected</div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 13, fontWeight: 600, color: C.navy }}>{domain}</div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, marginTop: 2 }}>
                {isAllMode
                  ? `${allTermCourses.length} courses loaded`
                  : courses.length
                    ? `${courses.length} course(s) found`
                    : "No courses selected"}
                {nameLoading ? " · Loading names…" : ` · ${Object.keys(nameIndex).length} people indexed`}
              </div>
            </div>
          )}

          {/* Term + Course picker */}
          {connected && (
            <div style={S.card}>
              <label style={S.label}>Academic Term</label>
              <select
                style={{ ...S.input, marginBottom: 10 }}
                value={selectedTermId}
                onChange={e => handleTermChange(e.target.value)}
                disabled={fetching}
              >
                {terms.map(t => (
                  <option key={t.id} value={String(t.id)}>{formatTermName(t.name)}</option>
                ))}
              </select>

              {/* All-courses button */}
              <button
                onClick={handleSelectAllCourses}
                disabled={fetching || loadingAllCourses || !selectedTermId}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  marginBottom: 10,
                  borderRadius: 2,
                  border: `1px solid ${isAllMode ? C.gold : C.border}`,
                  background: isAllMode ? "#fff8ed" : C.cream,
                  color: isAllMode ? C.gold : C.navy,
                  fontFamily: FONT_DATA,
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                {loadingAllCourses
                  ? "Loading all courses…"
                  : isAllMode
                    ? `✓ All ${allTermCourses.length} courses selected`
                    : "⬡ Query all courses in term"}
              </button>

              {/* Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1, height: 1, background: C.border }} />
                <span style={{ fontFamily: FONT_DATA, fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em" }}>or search</span>
                <div style={{ flex: 1, height: 1, background: C.border }} />
              </div>

              <label style={S.label}>Search Courses</label>
              <input
                style={{ ...S.input, marginBottom: courseSearch.length >= 2 ? 6 : 0 }}
                value={courseSearch}
                onChange={e => { handleCourseSearch(e.target.value); if (isAllMode) setCourseId(""); }}
                placeholder="Type to search (e.g. ENG, Math)…"
                disabled={fetching || !selectedTermId}
              />
{courseSearch.length >= 2 && courses.length > 0 && (
  <>
    <select
      style={{ ...S.input, marginBottom: 6 }}
      value={isAllMode ? "" : courseId}
      onChange={e => setCourseId(e.target.value)}
    >
      <option value="">— select a course —</option>
      {courses.map(c => (
        <option key={c.id} value={c.id}>
          {c.name || c.course_code || `Course ${c.id}`} (ID: {c.id})
        </option>
      ))}
    </select>
    
    {/* ADD THIS BUTTON */}
    <button
      onClick={handleSelectVisibleCourses}
      disabled={fetching}
      style={{
        width: "100%",
        padding: "8px 10px",
        marginBottom: 6,
        borderRadius: 2,
        border: `1px solid ${isAllMode && allTermCourses.length === courses.length ? C.gold : C.navy}`,
        background: isAllMode && allTermCourses.length === courses.length ? "#fff8ed" : "#e8f0f8",
        color: isAllMode && allTermCourses.length === courses.length ? C.gold : C.navy,
        fontFamily: FONT_DATA,
        fontSize: "0.82rem",
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
      }}
    >
      {isAllMode && allTermCourses.length === courses.length
        ? `✓ All ${courses.length} visible courses selected`
        : `⬡ Query all ${courses.length} visible courses`}
    </button>
    
    <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted, marginTop: 4 }}>
      {courses.length} course{courses.length !== 1 ? "s" : ""} found
    </div>
  </>
)}
            </div>
          )}

          {/* Quick fetch — only for single-course mode */}
          {connected && courseId && !isAllMode && (
            <div style={S.card}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>Quick Fetch + Analyze</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {QUICK_ACTIONS.map(a => (
                  <button key={a.label} style={S.btnGhost} onClick={() => handleQuickFetch(a)} disabled={fetching}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* All-courses hint */}
          {connected && isAllMode && (
            <div style={{ ...S.card, borderLeft: `3px solid ${C.gold}`, background: "#fffdf7" }}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.gold, marginBottom: 6 }}>Cross-Course Mode</div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                Queries will fetch enrollment grades from all {allTermCourses.length} courses in batches. Good for questions like "which courses have students below 75%?"
              </div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, color: C.muted, marginTop: 8, fontStyle: "italic" }}>
                Note: Quick Fetch is disabled in all-courses mode.
              </div>
            </div>
          )}

          {/* Activity log */}
          <div style={S.card}>
            <div style={{ fontFamily: FONT_DATA, fontSize: 9, fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: C.muted, marginBottom: 8 }}>Activity</div>
            <div style={S.logBox} ref={logRef}>
              {log.length === 0 && <span style={{ color: "#c8bfb0" }}>No activity yet.</span>}
              {log.map((l, i) => (
                <div key={i} style={{ color: l.type === "error" ? C.red : l.type === "success" ? C.green : C.muted }}>
                  <span style={{ color: "#c8bfb0" }}>{l.ts} </span>{l.msg}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div>
          <div style={S.piiNotice}>
            <span>🔒</span>
            <span>Names dehydrated before LLM · rehydrated for display only · raw data stays anonymized · type @ to mention someone</span>
          </div>

          {error && <div style={S.errorBox}>⚠ {error}</div>}

          {results.map((entry, idx) => (
            <ResultCard
              key={idx}
              entry={entry}
              idx={idx}
              total={results.length}
              onDownload={handleDownloadSingle}
              pdfLoading={pdfLoading}
              cardRef={el => { cardRefs.current[idx] = el; }}
            />
          ))}

          {fetching && (
            <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, padding: 32, textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontSize: "1.05rem", color: C.muted, fontStyle: "italic", marginBottom: 6 }}>Working…</div>
              <div style={{ fontFamily: FONT_DATA, fontSize: 11, color: "#c8bfb0" }}>Fetching Canvas data · scrubbing PII · analyzing</div>
            </div>
          )}

          {/* Query box */}
          <div style={{ background: "#fff", border: `1px solid ${C.border}`, borderRadius: 3, overflow: "hidden", boxShadow: "0 1px 8px rgba(0,0,0,0.05)" }}>

            <div style={{ background: C.bg, borderBottom: `1px solid ${C.border}`, padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontFamily: FONT_DATA, fontSize: 10, fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: C.muted }}>
                {isAllMode
                  ? `Ask across all ${allTermCourses.length} courses`
                  : hasResults ? "Ask a follow-up or new question" : "Ask about your course data"}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  onClick={() => setIncludeCharts(v => !v)}
                  title={includeCharts ? "Charts enabled — click to disable" : "Charts disabled — click to enable"}
                  style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 12px", border: `1px solid ${includeCharts ? C.gold : C.border}`, borderRadius: 2, background: includeCharts ? "#fff8ed" : C.cream, color: includeCharts ? C.gold : C.muted, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}
                >
                  <span style={{ fontSize: 13 }}>📊</span>
                  {includeCharts ? "Charts On" : "Charts Off"}
                </button>
                {hasResults && (
                  <button onClick={() => setShowThread(s => !s)}
                    style={{ fontFamily: FONT_DATA, fontSize: 11, fontWeight: 600, padding: "5px 14px", border: `1px solid ${showThread ? C.navy : C.border}`, borderRadius: 2, background: showThread ? "#e8f0f8" : C.cream, color: showThread ? C.navy : C.muted, cursor: "pointer" }}>
                    {showThread ? "▲ Hide Thread" : "▼ View Thread"}
                  </button>
                )}
              </div>
            </div>

            {showThread && history.length > 0 && (
              <div style={{ borderBottom: `1px solid ${C.border}`, maxHeight: 280, overflowY: "auto", padding: "14px 20px", background: "#fdfcf8", display: "flex", flexDirection: "column", gap: 10 }}>
                {history.map((msg, i) => (
                  <div key={i} style={{ display: "flex", gap: 10 }}>
                    <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: msg.role === "user" ? C.navy : C.gold, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_DATA, fontSize: 9, fontWeight: 700, color: "#fff" }}>
                      {msg.role === "user" ? "Q" : "AI"}
                    </div>
                    <div style={{ fontFamily: FONT_BODY, fontSize: 13, color: C.ink, lineHeight: 1.6, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, padding: "8px 12px", flex: 1 }}>
                      {msg.content.slice(0, 320)}{msg.content.length > 320 ? "…" : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ padding: "16px 20px" }}>
              <div style={{ position: "relative" }}>
                <textarea
                  ref={textareaRef}
                  value={query}
                  onChange={handleQueryChange}
                  onKeyDown={e => {
                    if (mentionOpen && (e.key === "Escape")) { setMentionOpen(false); return; }
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleQuery();
                  }}
                  placeholder={
                    isAllMode
                      ? "e.g. Which courses have students below 75%? (⌘+Enter to send)"
                      : hasResults
                        ? "Refine or ask something new… type @ to mention a student or staff (⌘+Enter to send)"
                        : "e.g. Show missing assignments for @John Smith (⌘+Enter to send)"
                  }
                  rows={2}
                  style={{ ...S.input, marginBottom: 10, resize: "none", lineHeight: 1.55 }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(isAllMode
                    ? [
                        "Which courses have students below 75%?",
                        "Show average grades by course",
                        "Which courses have the most at-risk students?",
                      ]
                    : EXAMPLE_QUERIES.slice(0, 3)
                  ).map(ex => (
                    <button key={ex} style={S.pill} onClick={() => handleQuery(ex)} disabled={fetching || !courseId}>
                      {ex}
                    </button>
                  ))}
                </div>
                <button
                  style={{ ...S.btnPrimary, padding: "8px 22px", fontSize: 13, opacity: (!query.trim() || !courseId || !connected) ? 0.5 : 1 }}
                  onClick={() => handleQuery()}
                  disabled={fetching || !query.trim() || !courseId || !connected}
                >
                  {fetching ? "Fetching…" : hasResults ? "Send →" : "Run Query →"}
                </button>
              </div>
            </div>
          </div>

          {!hasResults && !fetching && (
            <div style={{ marginTop: 16, padding: "16px 20px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 2, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, lineHeight: 1.75 }}>
              <strong style={{ fontFamily: FONT_DATA, fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", display: "block", marginBottom: 6 }}>How it works</strong>
              Connect to Canvas, then either pick a single course or click "Query all courses in term" for institution-wide questions. Type @ to mention a specific student or staff member — names are stripped before reaching the AI and rehydrated in the response for readability. Export any result or the full session as a formatted PDF report.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}